import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@marine/api-client/api';
import type { AIModel } from '@marine/contracts/types/ai.types';

/**
 * Platform-scoped OpenRouter catalog (Phase 39.2).
 *
 * Mirrors `useAIModels` from the tenant side but hits `/api/ai/models`
 * with a disjoint cache key (`['platform', 'ai', 'models']`) so tenant +
 * platform caches never cross-pollinate (D-11 + research §Q2). The server
 * uses a separate 24h Redis cache key (`platform_openrouter_models`) for the
 * same reason. 24h client-side staleTime matches the server TTL.
 *
 * `api.getList` already unwraps the `{data: [...]}` envelope — do NOT add
 * `select: (r) => r.data` (memory rule `feedback_api_envelope_unwrap.md`).
 */
export function usePlatformAIModels() {
  return useQuery({
    queryKey: ['platform', 'ai', 'models'],
    queryFn:  (): Promise<AIModel[]> => api.getList<AIModel>('/api/ai/models'),
    staleTime: 1000 * 60 * 60 * 24, // 24h — matches server-side cache TTL
  });
}

/**
 * Bust the platform OR catalog cache. Useful when a platform admin adds a
 * new OR key and wants the model list refreshed without waiting for the 24h
 * TTL to expire. The server busts its Redis cache; we replace React Query's
 * snapshot in-place so the UI updates without a flicker.
 */
export function useRefreshPlatformAIModels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<AIModel[]> =>
      api.postOne<AIModel[]>('/api/ai/models/refresh'),
    onSuccess: (data) => {
      qc.setQueryData(['platform', 'ai', 'models'], data);
    },
  });
}
